// packages/support-widget/src/SupportClient.ts
/**
 * Combined REST + WebSocket client for the AYILand support service.
 *
 * Owns reconnection logic, typed event emission, and the small REST surface
 * needed by the customer widget. Designed to be framework-agnostic — React
 * lives one level up (`useSupportClient`).
 */
import {
  SUPPORT_LIMITS,
  type ClientToServer,
  type CreateTicketInput,
  type ServerToClient,
  type SupportTicketDetailDTO,
  type SupportTicketSummaryDTO,
} from '@ayiland/support-types';

type Listener = (event: ServerToClient) => void;
type StatusListener = (status: SupportClientStatus) => void;

export type SupportClientStatus =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'connected'
  | 'reconnecting'
  | 'closed';

export interface SupportClientOptions {
  /** Base URL of @ayiland/support-service (e.g. `http://localhost:4200`). */
  serviceUrl: string;
  /** Returns the current JWT (re-evaluated on every reconnect). */
  getToken: () => string | null | undefined;
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

export class SupportClient {
  private ws: WebSocket | null = null;
  private status: SupportClientStatus = 'idle';
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectAttempts = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private stopped = false;

  constructor(private readonly opts: SupportClientOptions) {}

  // ── Status ──────────────────────────────────────────────────────────────
  getStatus(): SupportClientStatus {
    return this.status;
  }

  onStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }

  private setStatus(next: SupportClientStatus): void {
    this.status = next;
    for (const cb of this.statusListeners) cb(next);
  }

  // ── Events ──────────────────────────────────────────────────────────────
  on(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private dispatch(event: ServerToClient): void {
    for (const cb of this.listeners) cb(event);
  }

  // ── WebSocket lifecycle ─────────────────────────────────────────────────
  connect(): void {
    if (this.ws) return;
    this.stopped = false;
    this.openSocket();
  }

  disconnect(): void {
    this.stopped = true;
    this.clearHeartbeat();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
    }
    this.ws = null;
    this.setStatus('closed');
  }

  private openSocket(): void {
    this.setStatus(
      this.reconnectAttempts === 0 ? 'connecting' : 'reconnecting',
    );

    const wsUrl =
      this.opts.serviceUrl.replace(/^http/, 'ws').replace(/\/+$/, '') +
      SUPPORT_LIMITS.WS_PATH;

    const ws = new WebSocket(wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      const token = this.opts.getToken();
      if (!token) {
        this.dispatch({ type: 'auth_error', message: 'no_token' });
        ws.close();
        return;
      }
      this.setStatus('authenticating');
      ws.send(JSON.stringify({ type: 'auth', token } satisfies ClientToServer));
    };

    ws.onmessage = (ev) => {
      let parsed: ServerToClient;
      try {
        parsed = JSON.parse(ev.data) as ServerToClient;
      } catch {
        return;
      }
      if (parsed.type === 'auth_ok') {
        this.setStatus('connected');
        this.reconnectAttempts = 0;
        this.startHeartbeat();
      }
      if (parsed.type === 'auth_error') {
        // Don't loop on invalid token — let the host app handle it.
        this.stopped = true;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
      this.dispatch(parsed);
    };

    ws.onclose = () => {
      this.clearHeartbeat();
      this.ws = null;
      if (this.stopped) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose will follow and handle reconnect.
    };
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts += 1;
    const delay = Math.min(
      BACKOFF_MAX_MS,
      BACKOFF_BASE_MS * 2 ** Math.min(6, this.reconnectAttempts - 1),
    );
    this.setStatus('reconnecting');
    setTimeout(() => {
      if (!this.stopped) this.openSocket();
    }, delay);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeat = setInterval(() => {
      this.send({ type: 'ping' });
    }, SUPPORT_LIMITS.WS_PING_INTERVAL_MS);
  }

  private clearHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  // ── Outbound frames ─────────────────────────────────────────────────────
  send(frame: ClientToServer): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    try {
      this.ws.send(JSON.stringify(frame));
      return true;
    } catch {
      return false;
    }
  }

  join(ticketId: string): void {
    this.send({ type: 'join', ticketId });
  }

  leave(ticketId: string): void {
    this.send({ type: 'leave', ticketId });
  }

  postMessageWs(
    ticketId: string,
    body: string,
    clientId?: string,
  ): boolean {
    return this.send({ type: 'message', ticketId, body, clientId });
  }

  setTyping(ticketId: string, isTyping: boolean): void {
    this.send({ type: 'typing', ticketId, isTyping });
  }

  markRead(ticketId: string, messageId: string): void {
    this.send({ type: 'read', ticketId, messageId });
  }

  // ── REST helpers ────────────────────────────────────────────────────────
  private async rest<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const token = this.opts.getToken();
    const res = await fetch(`${this.opts.serviceUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean } & T;
    if (!res.ok || data.success === false) {
      const message =
        (data as { message?: string }).message ?? `HTTP ${res.status}`;
      throw new Error(message);
    }
    return data;
  }

  listTickets(): Promise<{ success: true; tickets: SupportTicketSummaryDTO[] }> {
    return this.rest('/api/support/tickets');
  }

  getTicket(
    id: string,
  ): Promise<{ success: true; ticket: SupportTicketDetailDTO }> {
    return this.rest(`/api/support/tickets/${id}`);
  }

  createTicket(
    input: CreateTicketInput,
  ): Promise<{ success: true; ticket: SupportTicketDetailDTO }> {
    return this.rest('/api/support/tickets', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  csat(
    ticketId: string,
    score: 1 | 2 | 3 | 4 | 5,
    comment?: string,
  ): Promise<{ success: true }> {
    return this.rest(`/api/support/tickets/${ticketId}/csat`, {
      method: 'POST',
      body: JSON.stringify({ score, comment }),
    });
  }

  async uploadAttachment(
    ticketId: string,
    file: File,
  ): Promise<{ success: true; url: string; filename: string }> {
    const token = this.opts.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      `${this.opts.serviceUrl}/api/support/tickets/${ticketId}/attachments`,
      {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      },
    );
    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      url?: string;
      filename?: string;
      message?: string;
    };
    if (!res.ok || data.success === false) {
      throw new Error(data.message ?? `HTTP ${res.status}`);
    }
    return data as { success: true; url: string; filename: string };
  }
}
